import * as yup from 'yup';
import { createOrdersComponentValidationSchema, OrdersConfig } from '@components/framework/orders/validation.ts';
import { openingHoursValidationSchema } from '@components/obsolete/OpeningHoursDefinition/OpeningHourDefinitionForm';
import { priceTableIssue } from '@utils/contractedPriceTable.ts';
import { ContractedCarrierForm, PriceTableForm } from '@components/ringil3/Features/Contracted/types.ts';
import { LineStop } from '@app/pages/customer/LineEdit/types.ts';
import { CargoTypeCodeEnum } from '@utils/constants.ts';

type Translate = (key: string, defaultText?: string, data?: any, count?: number) => string;

export interface LineValidationDeps {
  tr: Translate;
  ordersConfig: OrdersConfig;
}

/** Validation of the Linka form. Paths follow {@link LineForm}, so the shared route pieces find their messages. */
export const buildLineValidationSchema = ({ tr, ordersConfig }: LineValidationDeps) =>
  yup.object().shape({
    name: yup.string().trim().required(tr('LineEdit.nameRequired', 'Zadejte název linky')),

    locations: yup
      .array()
      .min(2, tr('LineEdit.minStops', 'Linka musí mít alespoň dvě zastávky'))
      .of(
        yup.object().shape({
          orders: yup.array().nullable().min(0).of(createOrdersComponentValidationSchema(ordersConfig, false, tr)),
          openingHours: openingHoursValidationSchema.nullable().default(undefined),
        }),
      ),

    cargoType: yup
      .object()
      .nullable()
      .required(tr('CreateExpedition.mandatoryCargoType', 'Cargo type is required'))
      .shape({
        cargoTypeCode: yup.string().nullable().required(tr('CreateExpedition.mandatoryCargoType', 'Cargo type is required')),
        adrWeight: yup.number().when('cargoTypeCode', {
          is: CargoTypeCodeEnum.ADR_LQ,
          then: schema => schema.required(),
          otherwise: schema => schema.notRequired().nullable(),
        }),
      }),

    capacity: yup
      .object()
      .nullable()
      .shape({
        ladenFactor: yup.number().nullable().integer(tr('LineEdit.capacityInteger', 'Kapacita musí být celé číslo')).min(1),
        cargoTemplateCode: yup
          .string()
          .nullable()
          .when('ladenFactor', {
            is: (lf: number | null) => !!lf,
            then: schema => schema.required(tr('LineEdit.capacityMuRequired', 'Vyberte manipulační jednotku kapacity')),
          }),
      }),

    intervalHours: yup
      .number()
      .typeError(tr('LineEdit.intervalHoursRequired', 'Zadejte platnost poptávky'))
      .nullable()
      .required(tr('LineEdit.intervalHoursRequired', 'Zadejte platnost poptávky'))
      .min(1, tr('LineEdit.intervalHoursRequired', 'Zadejte platnost poptávky')),

    contractedCarriers: yup
      .array()
      .min(1, tr('LineEdit.carrierRequired', 'Přidejte alespoň jednoho smluvního dopravce'))
      .test('carrier-selected', tr('LineEdit.carrierNotSelected', 'U každého nasmlouvaného dopravce vyberte dopravce'), (carriers: ContractedCarrierForm[]) =>
        (carriers ?? []).every(c => !!c.carrierId),
      ),

    priceTable: yup
      .mixed()
      .test('price-table', '', function (table) {
        const issue = priceTableIssue(table as PriceTableForm, tr);
        return issue ? this.createError({ message: issue }) : true;
      }),

    bothDirections: yup
      .boolean()
      .test('both-directions-ends', tr('LineEdit.bothDirectionsEnds', 'Obousměrná linka musí mít lokality na začátku i na konci trasy'), function (value) {
        if (!value) return true;
        const stops: LineStop[] = this.parent?.locations ?? [];
        const hasCandidates = (stop?: LineStop) => (stop?.candidates ?? []).some(c => !!c.locationId);
        return stops.length >= 2 && hasCandidates(stops[0]) && hasCandidates(stops[stops.length - 1]);
      }),
  });
